import { Component } from '@theme/component';

/**
 * Minimal fade slider with swipe support and autoplay
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} sliderDots
 *
 * @extends {Component<Refs>}
 */
export class FadeSlider extends Component {
  requiredRefs = ['slides'];

  /** @type {number | null} */
  #autoplayInterval = null;

  /** @type {number | null} */
  #progressRafId = null;

  /** @type {number} */
  currentIndex = 0;

  /** @type {number} */
  startX = 0;

  /** @type {number} */
  minSwipe = 30;

  connectedCallback() {
    super.connectedCallback();

    // Remove aria-hidden from first slide
    const { slides } = this.refs;

    if (slides?.[0]) {
      slides[0].removeAttribute('aria-hidden');
    }

    this.generatePagination(slides?.length);

    // Show first slide
    this.setActiveSlide(0);

    // Bind swipe events
    this.addEventListener('pointerdown', this.#handlePointerDown);

    // Start autoplay if enabled
    this.startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stopProgressAnimation();
    this.stopAutoplay();
  }

  startAutoplay() {
    if (!this.hasAttribute('autoplay')) return;

    const speed = parseInt(this.getAttribute('autoplay-speed') || '5', 10) * 1000;

    this.#autoplayInterval = setInterval(() => {
      this.setActiveSlide(this.currentIndex + 1);
    }, speed);
  }

  stopAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  /**
   * @param {number} count
   */
  generatePagination(count) {
    const { sliderDots } = this.refs;

    if (!sliderDots || count < 2) return;

    sliderDots.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.setAttribute('data-index', String(i));
      dot.classList.add('slider-dot');
      dot.addEventListener('click', () => {
        this.setActiveSlide(i);
        this.restartAutoplay();
      });
      sliderDots.appendChild(dot);
    }
  }

  /**
   * @param {number} index
   */
  setActiveSlide(index) {
    const { slides, sliderDots } = this.refs;
    if (!slides?.length) return;

    // Loop index first
    this.currentIndex = ((index % slides.length) + slides.length) % slides.length;

    // Update dots with normalized index
    if (sliderDots) {
      const dots = sliderDots.querySelectorAll('button');
      dots?.forEach?.((dot) => {
        dot.classList.remove('active');
        dot.style.removeProperty('--progress');
      });
      const activeDot = dots?.[this.currentIndex];
      if (activeDot) {
        activeDot.classList.add('active');
        if (this.hasAttribute('autoplay')) {
          this.#startProgressAnimation(activeDot);
        } else {
          activeDot.style.setProperty('--progress', '360deg');
        }
      }
    }

    // Update slides
    slides.forEach((slide, i) => {
      if (i === this.currentIndex) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('slide-change', {
        detail: { index: this.currentIndex },
      })
    );
  }

  /**
   * Stops any running JS-driven progress animation and resets the dot.
   */
  #stopProgressAnimation() {
    if (this.#progressRafId != null) {
      cancelAnimationFrame(this.#progressRafId);
      this.#progressRafId = null;
    }
  }

  /**
   * Runs a linear progress from 0deg to 360deg on the dot's --progress (for browsers without @property).
   * @param {HTMLElement} dot - The active slider dot element
   */
  #startProgressAnimation(dot) {
    this.#stopProgressAnimation();
    const durationMs = parseInt(this.getAttribute('autoplay-speed') || '5', 10) * 1000 || 5000;
    const startTime = performance.now();

    const tick = () => {
      const elapsed = performance.now() - startTime;
      const angle = Math.min(360, (elapsed / durationMs) * 360);
      dot.style.setProperty('--progress', `${angle}deg`);
      if (angle < 360) {
        this.#progressRafId = requestAnimationFrame(tick);
      } else {
        this.#progressRafId = null;
      }
    };
    dot.style.setProperty('--progress', '0deg');
    this.#progressRafId = requestAnimationFrame(tick);
  }

  /**
   * @param {PointerEvent} e
   */
  #handlePointerDown = (e) => {
    this.startX = e.clientX;
    let hasSwiped = false;

    /**
     * @param {PointerEvent} moveEvent
     */
    const handleMove = (moveEvent) => {
      if (hasSwiped) return;

      const deltaX = this.startX - moveEvent.clientX;

      if (Math.abs(deltaX) > this.minSwipe) {
        hasSwiped = true;
        const direction = deltaX > 0 ? 1 : -1;
        this.setActiveSlide(this.currentIndex + direction);
        this.restartAutoplay();
      }
    };

    const cleanup = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', cleanup);
      document.removeEventListener('pointercancel', cleanup);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', cleanup);
    document.addEventListener('pointercancel', cleanup);
  };
}

if (!customElements.get('fade-slider')) {
  customElements.define('fade-slider', FadeSlider);
}
